import React from "react";
import { blockMapper } from "../../../utils/templating/blockMapper";
import { ViewBlockGenerator } from "../../../utils/blockGenerators";

const VBG = ViewBlockGenerator as React.FC<any>;

/**
 * Renders the document body for PublishedPage V3 through the SAME engineering
 * the rest of the app uses — `blockMapper` (drilldown + section structure) +
 * `ViewBlockGenerator` (per-block view rendering, which already wires the
 * annotation-edit pipeline: onChange → handleAnnotationChange, allowReadOnly-
 * OnChange, enableAnnotationEditOnReadOnly). The only V3-specific bits are the
 * presentation (the host page's grid/cards) and the props threaded to the
 * editor via `blockProps` (v2 styling + onAnnotationClick + activeAnnoIds).
 *
 * Replaces Akash's hand-rolled `walkTplBlocks` flatten + DocumentFieldsCard
 * switch, which reimplemented blockMapper, bypassed ViewBlockGenerator (and
 * thus the pipeline), and lost each layout's structure.
 *
 * Layout-agnostic: blockMapper drills any tpl structure, so knowledgeResources2,
 * applicationFormsStyle1, and any future layout render as themselves. Which tpls
 * reach this renderer is decided by the dispatcher's layout-type gate upstream.
 *
 * `shouldRenderBlock` lets the host omit blocks from the body — used to pull
 * metadata/quant fields into the top card (separated-metadata mode) and to
 * honour the richtext-only field filter.
 */
export const FieldsRenderer: React.FC<{
  tpl: any;
  content: any;
  shouldRenderBlock?: (block: any) => boolean;
  blockProps?: Record<string, any>;
}> = ({ tpl, content, shouldRenderBlock, blockProps }) => {
  const body = tpl?.kp_templates?.body;
  // V3 presents a single scrollable document, so we flatten subSpaces (no
  // tabs) but keep blockMapper's within-page section structure intact.
  const subSpaceConfigs: any[] = body?.subSpaces?.length
    ? body.subSpaces.map((s: any) => s?.configs).filter(Boolean)
    : body?.configs
    ? [body.configs]
    : [];

  return (
    <>
      {subSpaceConfigs.map((configs, i) => (
        <React.Fragment key={i}>
          {blockMapper({
            content,
            activeSpace: { valuePath: "configs" },
            tplConfig: { configs },
            // No `onChange` → hidden sections just don't render (HiddenSection-
            // Clearer no-ops without it); we never clear values on a view page.
            blockGenerator: ({ block }: any) =>
              !shouldRenderBlock || shouldRenderBlock(block) ? (
                <VBG
                  key={block.props?.id ?? block.valuePath}
                  block={block}
                  content={content}
                  // Per-block spacing wrapper — same default the old layouts /
                  // FormConfigsRenderer use (PaddingTopBottom15), so inter-block
                  // gaps match the rest of the app.
                  Wrapper="PaddingTopBottom15"
                  blockProps={blockProps}
                />
              ) : null,
          })}
        </React.Fragment>
      ))}
    </>
  );
};

export default FieldsRenderer;
